import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MouseIcon from '@mui/icons-material/MouseOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';

import { THOR_COLORS } from '@/theme/thorTheme';
import type { CameraMode } from '@/types/scene.types';

interface SceneNavigationProps {
  cameraMode: CameraMode;
  startExpanded: boolean;
  collapseAfterInitialWaitSeconds?: number;
  collapseAfterClickSeconds?: number;
  startInitialCollapseTimerOnTrue?: boolean;
}

export const SceneNavigation = ({
  cameraMode,
  startExpanded,
  collapseAfterInitialWaitSeconds = -1,
  collapseAfterClickSeconds = -1,
  startInitialCollapseTimerOnTrue = false,
}: SceneNavigationProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const prevStartInitialCollapseTimerOnTrue = useRef<boolean>(false);
  const [chanceToReadAll, setChanceToReadAll] = useState(false);
  const autoHideTimer = useRef<number | null>(null);
  const [shouldStackVertically, setShouldStackVertically] = useState(false);
  const [shouldStackVerticallyBeforeAnimation, setShouldStackVerticallyBeforeAnimation] =
    useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Get instructions based on camera mode
  const getInstructions = () => {
    switch (cameraMode) {
      case '1st-person':
        return 'Left click + drag to look, right click + drag to move, scroll to zoom. Play button to start action';
      case '3rd-person':
        return 'Click highlighted object to interact.\u00A0 Left click + drag to rotate.\u00A0 Scroll to zoom.';
      case 'top-down':
        return 'Select an eye in the scene to interact with an object.';
      default:
        return 'Click and drag to move camera';
    }
  };

  useEffect(() => {
    if (startExpanded) {
      handleSetExpanded(true, false, true);
    }
    return () => {
      // optional cleanup on unmount
    };
  }, []);

  useEffect(() => {
    if (!prevStartInitialCollapseTimerOnTrue.current && startInitialCollapseTimerOnTrue === true) {
      // runs ONLY when 'startInitialCollapseTimerOnTrue' false → true
      setChanceToReadAll(true);
    }
    prevStartInitialCollapseTimerOnTrue.current = startInitialCollapseTimerOnTrue;
  }, [startInitialCollapseTimerOnTrue]);

  useEffect(() => {
    if (chanceToReadAll) {
      handleSetExpanded(isExpanded, true, true);
    }
  }, [chanceToReadAll]);

  // check if content fits and adjust layout on resize
  useEffect(() => {
    const checkFit = () => {
      if (!isExpanded || !containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const availableWidth = viewportWidth - containerRect.left - 20; // 20px margin

      // only check on resize, not on initial open
      // if horizontal would overflow, stack vertically
      // close in extreme cases (viewport < 200px)
      if (viewportWidth < 200) {
        // extremely small, close
        setIsExpanded(false);
      } else if (availableWidth < 580 && !shouldStackVertically) {
        // not enough space for horizontal, stack vertically
        setShouldStackVertically(true);
      } else if (availableWidth >= 580 && shouldStackVertically) {
        // has space now, switch back to horizontal
        setShouldStackVertically(false);
      }
    };

    if (isExpanded) {
      // only listen to resize, don't check immediately on open
      window.addEventListener('resize', checkFit);
      return () => {
        window.removeEventListener('resize', checkFit);
      };
    }
    return;
  }, [isExpanded, shouldStackVertically]);

  const handleSetExpanded = (
    newIsExpanded: boolean,
    collapseTimeout: boolean = false,
    firstTime: boolean = false
  ) => {
    console.log('--------- expanding');
    setIsExpanded(newIsExpanded);
    if (newIsExpanded) {
      // check available space to decide layout before rendering
      // only set vertical stacking, never close on open
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const availableWidth = viewportWidth - containerRect.left - 20;
        // rough estimate: horizontal needs ~580px, vertical needs ~240px
        if (availableWidth < 580) {
          setShouldStackVertically(true);
        } else {
          setShouldStackVertically(false);
        }
      }

      if (autoHideTimer.current) {
        window.clearTimeout(autoHideTimer.current);
      }
      let hideTimeoutSeconds = firstTime
        ? collapseAfterInitialWaitSeconds
        : collapseAfterClickSeconds;

      console.log(
        `++++++++++++++++++++ about to set timeout timeout chanceToReadAll: ${chanceToReadAll}`
      );
      if (hideTimeoutSeconds > 0 && collapseTimeout && chanceToReadAll) {
        console.log(`++++++++++++++++++++ setting timeout ${hideTimeoutSeconds}`);
        autoHideTimer.current = window.setTimeout(() => {
          setIsExpanded(false);
          autoHideTimer.current = null;
        }, hideTimeoutSeconds * 1000);
      }
    } else {
      setShouldStackVertically(false);
    }
  };

  const handleToggle = () => {
    let newState = !isExpanded;
    handleSetExpanded(newState, true);
  };

  const alphaCollapsedNoHovered = 0.6;
  const alphaCollapsedHovered = 0.95;
  const alphaExpandedNotHovered = 0.9;
  const alphaExpandedHovered = 0.95;

  const alpha = isExpanded
    ? !isHovered
      ? alphaExpandedNotHovered
      : alphaExpandedHovered
    : !isHovered
      ? alphaCollapsedNoHovered
      : alphaCollapsedHovered;

  return (
    <Box
      ref={containerRef}
      sx={{
        // position: 'absolute',
        // top: '36px',
        // left: '36px',

        // bottom: '32px',
        // left: '36px',

        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        height: 40,
      }}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      {/* Compact question mark button */}
      <IconButton
        onClick={handleToggle}
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: THOR_COLORS.darkGreen09,
          opacity: alpha,
          transition: 'all 0.2s ease-in-out',
          transform: isHovered ? 'scale(1.1)' : 'scale(1)',
          '&:hover': {
            backgroundColor: THOR_COLORS.darkGreen09,
            opacity: alphaCollapsedHovered,
          },
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: 20, color: '#fff' }} />
      </IconButton>

      {/* Expandable tooltip content */}
      <Box
        onClick={handleToggle}
        sx={{
          overflow: 'visible',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        <Collapse
          in={isExpanded}
          orientation="horizontal"
          timeout={300}
          onEnter={() => {
            // called when expansion animation is about to start
            if (shouldStackVertically) {
              setShouldStackVerticallyBeforeAnimation(shouldStackVertically);
              setShouldStackVertically(false);
            }
            // console.log('rxpansion animation starting');
          }}
          // onExit={() => {
          //   // called when collapse animation is about to start
          //   console.log('Collapse animation starting');
          // }}
          onEntered={() => {
            // called when expansion animation finishes
            setShouldStackVertically(shouldStackVerticallyBeforeAnimation);
            // console.log('expansion animation completed');
          }}
          onExited={() => {
            // called when collapse animation finishes
            setShouldStackVerticallyBeforeAnimation(false);
            console.log('Collapse animation completed');
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <Box
            ref={contentRef}
            sx={{
              ml: 1,
              borderRadius: 4,
              backgroundColor: THOR_COLORS.darkGreen09,
              padding: 2,
              maxWidth: '600px',
              opacity: alpha,
            }}
          >
            <Stack
              direction={shouldStackVertically ? 'column' : 'row'}
              spacing={shouldStackVertically ? 1 : 2}
              alignItems={shouldStackVertically ? 'flex-start' : 'center'}
            >
              <MouseIcon sx={{ flexShrink: 0 }} />
              <Typography
                onClick={handleToggle}
                variant="body1"
                sx={{
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  whiteSpace: shouldStackVertically ? 'normal' : 'nowrap',
                }}
              >
                {getInstructions()}
              </Typography>
            </Stack>
          </Box>
        </Collapse>
        {isExpanded && (
          <IconButton
            sx={{
              position: 'absolute',
              top: -16,
              right: -16,
              padding: 0.5,
              zIndex: 999,
              opacity: alpha,
              color: '#fff',
              backgroundColor: THOR_COLORS.darkGreen09,
              width: 32,
              height: 32,
              transform: isHovered ? 'scale(1.1)' : 'scale(1)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: THOR_COLORS.darkGreen09,
                opacity: alphaExpandedHovered,
                // transform: 'scale(1.1)'
              },
            }}
          >
            <HighlightOffIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
